import { observer } from 'mobx-react-lite';
import React, { useState } from 'react';
import { useMst } from '../../../models/stores/Root';
import { PromoCodeApplyResponse } from '../../../models/stores/sub-stores/Cart';
import LoadingView from '../../../pages/views/LoadingView';
import CollapseIcon from '../../content/CollapseIcon';
import ExpandIcon from '../../content/ExpandIcon';
import Button from '../Button';
import LabeledTextbox from '../LabeledTextbox';

export const PromoCodeTestId = 'PromoCode-input';
export const PromoCodeExpandButtonTestId = 'PromoCode-expand-button-input';
export const PromoCodeCollapseButtonTestId = 'PromoCode-collapse-button-input';

export type PromoCodeProps = {
  appliedCouponCodes?: string[];
  defaultValue?: string;
  onPromoCodeApplyClicked: (
    promoCode: string
  ) => Promise<PromoCodeApplyResponse>;
};

const PromoCode: React.FC<PromoCodeProps> = observer(
  ({
    appliedCouponCodes = [],
    defaultValue,
    onPromoCodeApplyClicked,
  }: PromoCodeProps) => {
    const rootStore = useMst();
    const [isOpen, setIsOpen] = useState(false);
    const [applyingPromoCode, setApplyingPromoCode] = useState(false);
    const [buttonAnimationClass, setButtonAnimationClass] = useState('');
    const [contentOpacityAnimationClass, setContentOpacityAnimation] =
      useState('');
    const [promoCode, setPromoCode] = useState<string | undefined>(
      defaultValue || ''
    );
    const [errorMessage, setErrorMessage] = useState<string | undefined>();
    const [isCouponCodeAlreadyApplied, setIsCouponCodeAlreadyApplied] =
      useState<boolean>(
        Boolean(
          promoCode &&
            !!appliedCouponCodes.find(
              code => code.toLowerCase() === promoCode.toLowerCase()
            )
        )
      );
    const [warningMessage, setWarningMessage] = useState<string | undefined>(
      isCouponCodeAlreadyApplied
        ? `Promo code "${promoCode}" is already applied.`
        : undefined
    );

    const handleApplyPromoCodeClicked = async () => {
      if (!promoCode) {
        return;
      }
      setApplyingPromoCode(true);
      setErrorMessage('');
      setWarningMessage('');
      const response = await onPromoCodeApplyClicked(promoCode);
      if (!response?.applied) {
        setErrorMessage(response?.errorMessage);
      } else {
        setPromoCode('');
        setWarningMessage(response?.errorMessage);
      }
      await rootStore.putStripeIntentFlow();
      setApplyingPromoCode(false);
    };

    const handlePromoCodeChanged = (e: React.ChangeEvent<HTMLInputElement>) => {
      const { value } = e.target;
      setPromoCode(value);
      if (
        value &&
        !!appliedCouponCodes.find(
          code => code.toLowerCase() === value.toLowerCase()
        )
      ) {
        setWarningMessage(`Promo code "${value}" is already applied.`);
        setIsCouponCodeAlreadyApplied(true);
      } else {
        setWarningMessage('');
        setIsCouponCodeAlreadyApplied(false);
      }
    };

    const handlePromoCodeKeyPress = (
      e: React.KeyboardEvent<HTMLInputElement>
    ) => {
      if (e.key === 'Enter' && !isCouponCodeAlreadyApplied) {
        handleApplyPromoCodeClicked();
      }
    };

    const handleExpandCollapseButtonClicked = () => {
      setButtonAnimationClass(!isOpen ? 'animate-expand' : 'animate-collapse');
      setContentOpacityAnimation(
        !isOpen ? 'animate-appear' : 'animate-disappear'
      );
    };

    const handleButtonAnimationEnd = () => {
      setButtonAnimationClass('');
      setContentOpacityAnimation('');
      setIsOpen(value => {
        // when closing empty promoCode and errorMessage should be cleared
        if (value) {
          setPromoCode('');
          setErrorMessage('');
          setWarningMessage('');
        }
        return !value;
      });
    };

    return (
      <div className="flex flex-row" data-testid={PromoCodeTestId}>
        <LoadingView
          isOpen={applyingPromoCode}
          message="Applying discount..."
        />
        <button
          type="button"
          className="portrait:pt-0 sm:pt-2"
          onClick={handleExpandCollapseButtonClicked}
          data-testid={
            isOpen ? PromoCodeCollapseButtonTestId : PromoCodeExpandButtonTestId
          }
          onAnimationEnd={handleButtonAnimationEnd}
        >
          <div className="px-2 flex flex-row pb-4 h-16 justify-items-center">
            <span
              className={`self-center pt-2 mt-1 ${buttonAnimationClass} ${
                isOpen && 'mt-2'
              }`}
            >
              {!isOpen && <ExpandIcon />}
              {isOpen && <CollapseIcon />}
            </span>
            {!isOpen && (
              <span className="pt-1 pl-2 mt-2 self-center">Promo Code</span>
            )}
          </div>
        </button>
        {isOpen && (
          <div
            className={`flex flex-col w-full ${contentOpacityAnimationClass}`}
          >
            <div className="flex flex-row">
              <span className="w-3/4 self-center">
                <LabeledTextbox
                  className="mb-0"
                  label="Promo Code"
                  name="promoCode"
                  maxLength={25}
                  value={promoCode || ''}
                  errorLabel={errorMessage}
                  onChange={handlePromoCodeChanged}
                  onKeyPress={handlePromoCodeKeyPress}
                  autoFocus
                />
              </span>
              <Button
                className="self-center mt-3"
                isDisabled={
                  !promoCode || applyingPromoCode || isCouponCodeAlreadyApplied
                }
                onClick={handleApplyPromoCodeClicked}
              >
                Apply
              </Button>
            </div>
            {warningMessage && (
              <span className="text-sm pl-2">{warningMessage}</span>
            )}
          </div>
        )}
      </div>
    );
  }
);

export default PromoCode;
